import React, { useCallback, useEffect, useState } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditIcon from '@mui/icons-material/Edit';
import FolderIcon from '@mui/icons-material/Folder';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import ProjectStatusChip from 'components/chip/ProjectStatusChip';
import {
  ProjectDetailResult,
  ProjectListResult,
  ProjectStatus,
  getProject,
  listProject,
} from 'stores/ProjectStoreAdapter';
import { formatDate } from 'utils/DateTimeFormatter';

const statusTabs: { value: ProjectStatus; label: string; headerLabel: string }[] = [
  { value: 'PREPARING', label: '準備中', headerLabel: '準備中のプロジェクト' },
  { value: 'ACTIVE', label: '稼働中', headerLabel: '稼働中のプロジェクト' },
  { value: 'CLOSED', label: '終了', headerLabel: '終了したプロジェクト' },
];

type ActiveProjectsDashboardProps = {
  /** 課長のユーザーID */
  managerId: string;
};

/**
 * 課長ロールのユーザー向け：ステータス切り替え式のプロジェクト一覧ダッシュボード。
 * プロジェクトクリックで簡易詳細表示。
 */
const ActiveProjectsDashboard: React.FC<ActiveProjectsDashboardProps> = ({ managerId }) => {
  const [activeStatus, setActiveStatus] = useState<ProjectStatus>('ACTIVE');
  const [projects, setProjects] = useState<ProjectListResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [projectDetail, setProjectDetail] = useState<ProjectDetailResult | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchProjects = useCallback(
    (status: ProjectStatus) => {
      setLoading(true);
      setSelectedIndex(null);
      setProjectDetail(null);
      listProject({ managerId, status })
        .then((result) => {
          setProjects(result);
        })
        .catch(() => {
          setProjects([]);
        })
        .finally(() => setLoading(false));
    },
    [managerId],
  );

  useEffect(() => {
    fetchProjects(activeStatus);
  }, [activeStatus, fetchProjects]);

  const handleStatusPrev = () => {
    const idx = statusTabs.findIndex((t) => t.value === activeStatus);
    const prevIdx = (idx - 1 + statusTabs.length) % statusTabs.length;
    setActiveStatus(statusTabs[prevIdx].value);
  };

  const handleStatusNext = () => {
    const idx = statusTabs.findIndex((t) => t.value === activeStatus);
    const nextIdx = (idx + 1) % statusTabs.length;
    setActiveStatus(statusTabs[nextIdx].value);
  };

  const selectedProject = selectedIndex !== null ? projects[selectedIndex] : null;

  // 選択変更時に詳細を取得
  useEffect(() => {
    if (selectedProject) {
      setDetailLoading(true);
      getProject({ projectId: selectedProject.projectId })
        .then(setProjectDetail)
        .catch(() => setProjectDetail(null))
        .finally(() => setDetailLoading(false));
    } else {
      setProjectDetail(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex, selectedProject?.projectId]);

  const handleSelectProject = (idx: number) => {
    setSelectedIndex(idx);
  };

  const handleProjectPrev = () => {
    if (selectedIndex !== null && selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1);
    }
  };

  const handleProjectNext = () => {
    if (selectedIndex !== null && selectedIndex < projects.length - 1) {
      setSelectedIndex(selectedIndex + 1);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'auto',
        width: 520,
        height: 300,
        minWidth: 320,
        minHeight: 200,
        resize: 'both',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* ヘッダー / ナビゲーション */}
      {selectedProject ? (
        // 詳細表示時: 戻る + 前後ナビ
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 1.5, py: 0.75, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tooltip title="一覧に戻る">
            <IconButton size="small" onClick={() => setSelectedIndex(null)}>
              <ArrowBackIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Tooltip title="前のプロジェクト">
              <span>
                <IconButton size="small" onClick={handleProjectPrev} disabled={selectedIndex === 0}>
                  <ChevronLeftIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Typography variant="caption" color="text.secondary">
              {(selectedIndex ?? 0) + 1} / {projects.length}
            </Typography>
            <Tooltip title="次のプロジェクト">
              <span>
                <IconButton
                  size="small"
                  onClick={handleProjectNext}
                  disabled={selectedIndex === projects.length - 1}
                >
                  <ChevronRightIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          <Tooltip title="編集画面を開く">
            <IconButton
              size="small"
              onClick={() => window.open(`/member/project/${selectedProject.projectId}`, '_blank')}
            >
              <EditIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      ) : (
        // 一覧表示時: ヘッダー
        <>
          {/* ヘッダー + ステータス切り替え: 1行にまとめ */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
              <FolderIcon sx={{ fontSize: 18, color: 'primary.main', flexShrink: 0 }} />
              <Typography variant="body2" fontWeight={700} noWrap>
                {statusTabs.find((t) => t.value === activeStatus)?.headerLabel}
              </Typography>
              {!loading && (
                <Chip
                  label={projects.length}
                  size="small"
                  color="primary"
                  sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, flexShrink: 0 }}
                />
              )}
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0}>
              <IconButton size="small" onClick={handleStatusPrev}>
                <ChevronLeftIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <IconButton size="small" onClick={handleStatusNext}>
                <ChevronRightIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Stack>
          </Stack>
        </>
      )}

      {/* コンテンツ */}
      {selectedProject ? (
        // 簡易詳細表示
        detailLoading ? (
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : projectDetail ? (
          <Box sx={{ px: 2.5, py: 2, overflow: 'auto', flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
              <Typography variant="body2" fontWeight={700} noWrap sx={{ flex: 1 }}>
                {projectDetail.projectName}
              </Typography>
              <ProjectStatusChip status={projectDetail.status} />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
              {projectDetail.projectNameKana}
            </Typography>
            <Stack spacing={0.75}>
              <DetailRow label="取引先" value={projectDetail.client.clientName} />
              <DetailRow
                label="期間"
                value={`${projectDetail.startDate ? formatDate(projectDetail.startDate) : '未定'} 〜 ${projectDetail.endDate ? formatDate(projectDetail.endDate) : ''}`}
              />
              <DetailRow label="作成日" value={formatDate(projectDetail.createdAt)} />
              <DetailRow label="更新日" value={formatDate(projectDetail.updatedAt)} />
            </Stack>

            {/* 業界 */}
            {projectDetail.industries && projectDetail.industries.length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  業界
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                  {projectDetail.industries.map((ind) => (
                    <Chip
                      key={ind.industryId}
                      label={ind.industryName}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {/* 技術スタック（分類ごと） */}
            {projectDetail.techStacks && projectDetail.techStacks.length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  技術スタック
                </Typography>
                {Object.entries(
                  projectDetail.techStacks.reduce(
                    (acc, ts) => {
                      const key = ts.categoryLabel;
                      if (!acc[key]) acc[key] = [];
                      acc[key].push(ts);
                      return acc;
                    },
                    {} as Record<string, typeof projectDetail.techStacks>,
                  ),
                ).map(([category, items]) => (
                  <Box key={category} sx={{ mt: 0.75 }}>
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{ fontSize: '0.65rem' }}
                    >
                      {category}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={0.5}
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ mt: 0.25 }}
                    >
                      {items.map((ts) => (
                        <Chip
                          key={`${ts.techStackId}-${ts.categoryCode}`}
                          label={ts.techStackName}
                          size="small"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ flex: 1 }} />
        )
      ) : loading ? (
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress size={24} />
        </Box>
      ) : projects.length > 0 ? (
        <Stack divider={<Divider />} sx={{ overflow: 'auto', flex: 1 }}>
          {projects.map((project, idx) => (
            <Box
              key={project.projectId}
              onClick={() => handleSelectProject(idx)}
              sx={{
                px: 2,
                py: 1.25,
                cursor: 'pointer',
                transition: 'background-color 0.15s',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Typography variant="body2" fontWeight={600} noWrap>
                {project.projectName}
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mt: 0.25 }}>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {project.clientName}
                </Typography>
                {project.startDate && (
                  <Typography variant="caption" color="text.disabled" noWrap>
                    {formatDate(project.startDate)}
                  </Typography>
                )}
              </Stack>
            </Box>
          ))}
        </Stack>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            {statusTabs.find((t) => t.value === activeStatus)?.headerLabel}はありません
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

// 簡易詳細の行コンポーネント
function DetailRow({
  label,
  value,
  children,
}: {
  label: string;
  value?: string;
  children?: React.ReactNode;
}) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ width: 48, flexShrink: 0, textAlign: 'right' }}
        noWrap
      >
        {label}
      </Typography>
      {children || (
        <Typography variant="body2" fontWeight={500} noWrap>
          {value || '-'}
        </Typography>
      )}
    </Stack>
  );
}

export default ActiveProjectsDashboard;
